/**
 * CalendarEventDetailModal — displays event details with optional editing.
 * Shown when clicking an event in viewer mode.
 * Editable fields when writeback is enabled.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { resolveEventColor, type CalendarEvent } from './calendarEventPositioning';
import { pmTheme } from './pmTheme';

interface CalendarEventDetailModalProps {
  event: CalendarEvent;
  colorMap?: Record<string, string>;
  writebackEnabled?: boolean;
  onClose: () => void;
  onSave?: (eventId: string, updates: Record<string, unknown>) => void;
  onDelete?: (eventId: string) => void;
}

export const CalendarEventDetailModal: React.FC<CalendarEventDetailModalProps> = ({
  event,
  colorMap,
  writebackEnabled = false,
  onClose,
  onSave,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({
    title: event.title || '',
    description: String(event.description || ''),
    location: String(event.location || ''),
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const color = resolveEventColor(event.category, colorMap);

  // Escape key to close + focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // Focus the modal on mount
    modalRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const formatDateTime = (dateStr: string): string => {
    const date = parseISO(dateStr);
    if (!isValid(date)) return dateStr;
    if (dateStr.includes('T')) {
      return format(date, 'EEEE, MMMM d, yyyy · h:mm a');
    }
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(event.id, editValues);
    }
    setIsEditing(false);
  }, [event.id, editValues, onSave]);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(event.id);
    }
    onClose();
  }, [event.id, onDelete, onClose]);

  return (
    <div
      data-testid="calendar-event-detail-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)' }} />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Event details: ${event.title || 'Untitled'}`}
        tabIndex={-1}
        style={{
          position: 'relative',
          backgroundColor: pmTheme.surface,
          color: pmTheme.text,
          borderRadius: '12px',
          width: 420,
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          outline: 'none',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Color strip */}
        <div style={{ height: 4, backgroundColor: color, borderRadius: '12px 12px 0 0' }} />

        {/* Content */}
        <div style={{ padding: '20px 24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            {isEditing ? (
              <input
                data-testid="calendar-event-edit-title"
                value={editValues.title}
                onChange={e => setEditValues(v => ({ ...v, title: e.target.value }))}
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: pmTheme.text,
                  backgroundColor: pmTheme.surface,
                  border: `1px solid ${pmTheme.input}`,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  flex: 1,
                  marginRight: 8,
                }}
              />
            ) : (
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: pmTheme.text, margin: 0, flex: 1 }}>
                {event.title || '(No title)'}
              </h3>
            )}
            <button
              data-testid="calendar-event-close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: pmTheme.textSubtle,
                cursor: 'pointer',
                padding: '0 4px',
                lineHeight: 1,
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {/* Time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: '16px' }}>&#128339;</span>
            <div>
              <div style={{ fontSize: '13px', color: pmTheme.text }}>
                {formatDateTime(event.start)}
              </div>
              {event.end && (
                <div style={{ fontSize: '12px', color: pmTheme.textMuted }}>
                  to {formatDateTime(event.end)}
                </div>
              )}
              {event.allDay && (
                <span style={{ fontSize: '11px', color: pmTheme.textMuted, fontStyle: 'italic' }}>All day</span>
              )}
            </div>
          </div>

          {/* Location */}
          {(!!event.location || isEditing) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '16px' }}>&#128205;</span>
              {isEditing ? (
                <input
                  data-testid="calendar-event-edit-location"
                  value={editValues.location}
                  onChange={e => setEditValues(v => ({ ...v, location: e.target.value }))}
                  placeholder="Add location"
                  style={{
                    fontSize: '13px',
                    color: pmTheme.text,
                    backgroundColor: pmTheme.surface,
                    border: `1px solid ${pmTheme.input}`,
                    borderRadius: '6px',
                    padding: '4px 8px',
                    flex: 1,
                  }}
                />
              ) : (
                <span style={{ fontSize: '13px', color: pmTheme.text }}>{String(event.location)}</span>
              )}
            </div>
          )}

          {/* Category */}
          {!!event.category && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '16px' }}>&#127991;</span>
              <span
                style={{
                  fontSize: '12px',
                  backgroundColor: `${color}20`,
                  color: color,
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontWeight: 500,
                }}
              >
                {event.category}
              </span>
            </div>
          )}

          {/* Assignee */}
          {!!event.assignee && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '16px' }}>&#128100;</span>
              <span style={{ fontSize: '13px', color: pmTheme.text }}>{String(event.assignee)}</span>
            </div>
          )}

          {/* Description */}
          {(!!event.description || isEditing) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '11px', color: pmTheme.textSubtle, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Description
              </div>
              {isEditing ? (
                <textarea
                  data-testid="calendar-event-edit-description"
                  value={editValues.description}
                  onChange={e => setEditValues(v => ({ ...v, description: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%',
                    fontSize: '13px',
                    color: pmTheme.text,
                    backgroundColor: pmTheme.surface,
                    border: `1px solid ${pmTheme.input}`,
                    borderRadius: '6px',
                    padding: '8px',
                    resize: 'vertical',
                  }}
                />
              ) : (
                <p style={{ fontSize: '13px', color: pmTheme.textMuted, margin: 0, lineHeight: 1.5 }}>
                  {String(event.description)}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          {writebackEnabled && (
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${pmTheme.borderSubtle}` }}>
              <div>
                {!showDeleteConfirm ? (
                  <button
                    data-testid="calendar-event-delete"
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      fontSize: '12px',
                      color: pmTheme.danger,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px 0',
                    }}
                  >
                    Delete event
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-testid="calendar-event-confirm-delete"
                      onClick={handleDelete}
                      style={{
                        fontSize: '12px',
                        color: pmTheme.textInverse,
                        backgroundColor: pmTheme.danger,
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      Confirm delete
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      style={{
                        fontSize: '12px',
                        color: pmTheme.textMuted,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      style={{
                        fontSize: '12px',
                        color: pmTheme.textMuted,
                        backgroundColor: pmTheme.surfaceMuted,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="calendar-event-save"
                      onClick={handleSave}
                      style={{
                        fontSize: '12px',
                        color: pmTheme.textInverse,
                        backgroundColor: pmTheme.primary,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    data-testid="calendar-event-edit"
                    onClick={() => setIsEditing(true)}
                    style={{
                      fontSize: '12px',
                      color: pmTheme.primary,
                      backgroundColor: pmTheme.primarySoft,
                      border: `1px solid ${pmTheme.primaryBorder}`,
                      borderRadius: '6px',
                      padding: '6px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
